import React, { useState, useEffect } from "react";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import AppBreadcrumb from "../../../Components/Common/AppBreadcrumb";
import {
  selectCostTypes,
  selectCategories,
  selectSubCategories,
  selectConfigLoading,
  selectBudgets,
  selectBudgetsLoading,
} from "../../../slices/vendorInvoiceConfig/selectors";
import {
  fetchCostTypesAndCategories,
  createCostType, updateCostType, deleteCostType,
  createCategory, updateCategory, deleteCategory,
  createSubCategory, updateSubCategory, deleteSubCategory,
  fetchBudgetsByPhase, saveBudget, removeBudget,
} from "../../../slices/vendorInvoiceConfig/thunk";
import { selectPhases } from "../../../slices/phases/selectors";
import { getPhases } from "../../../slices/thunks";
import { http } from "../../../helpers/api/http";

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCENT_COLORS = ["#002045", "#7c3aed", "#0891b2", "#059669", "#dc2626", "#d97706"];
const BADGE_BG = {
  "#002045": "#dce8ff", "#7c3aed": "#f3e8ff", "#0891b2": "#e0f2fe",
  "#059669": "#d1fae5", "#dc2626": "#fee2e2", "#d97706": "#fef3c7",
};
const PAGE_SIZE = 10;
const EMPTY_CATEGORY_MODAL = { open: false, mode: "add", data: { name: "", cost_type: "" } };
const EMPTY_ADD_FORM = { sub_category: "", block: "", amount: "", currency: "EUR" };

function getAccent(index) { return ACCENT_COLORS[index % ACCENT_COLORS.length]; }
function getBadgeBg(color) { return BADGE_BG[color] || "#f0f4ff"; }

const formatWithCommas = (raw) => {
  if (!raw && raw !== 0) return "";
  const str = String(raw).replace(/,/g, "");
  const [integer, decimal] = str.split(".");
  const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (decimal !== undefined) {
    const trimmed = decimal.replace(/0+$/, "");
    if (trimmed) return `${formatted}.${trimmed}`;
  }
  return formatted;
};

const SectionLabel = ({ children }) => (
  <h6 style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 14 }}>
    {children}
  </h6>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const CostCategories = () => {
  const dispatch = useDispatch();
  const costTypes = useSelector(selectCostTypes);
  const categories = useSelector(selectCategories);
  const subCategories = useSelector(selectSubCategories);
  const loading = useSelector(selectConfigLoading);
  const budgets = useSelector(selectBudgets);
  const budgetsLoading = useSelector(selectBudgetsLoading);
  const phases = useSelector(selectPhases);

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("categories");

  // Cost-type state
  const [addCostTypeOpen, setAddCostTypeOpen] = useState(false);
  const [addCostTypeInput, setAddCostTypeInput] = useState("");
  const [editingCostType, setEditingCostType] = useState(null);

  // Sub-category state
  const [addSubCatOpen, setAddSubCatOpen] = useState(false);
  const [addSubCatInput, setAddSubCatInput] = useState("");
  const [editingSubCat, setEditingSubCat] = useState(null);

  // Category modal
  const [categoryModal, setCategoryModal] = useState(EMPTY_CATEGORY_MODAL);

  // Categories filter + pagination
  const [selectedCostType, setSelectedCostType] = useState("all");
  const [page, setPage] = useState(1);

  // Budgets tab
  const [budgetPhase, setBudgetPhase] = useState("");
  const [phaseBlocks, setPhaseBlocks] = useState([]);
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [addForms, setAddForms] = useState({});

  useEffect(() => {
    dispatch(fetchCostTypesAndCategories());
    dispatch(getPhases());
  }, [dispatch]);

  // Fetch budgets + blocks when phase changes
  useEffect(() => {
    if (budgetPhase) {
      dispatch(fetchBudgetsByPhase(budgetPhase));
      http.get(`/phases/${budgetPhase}/`).then((res) => setPhaseBlocks(res.blocks || [])).catch(() => setPhaseBlocks([]));
    } else {
      setPhaseBlocks([]);
      setExpandedCats(new Set());
      setAddForms({});
    }
  }, [budgetPhase, dispatch]);

  // ── Cost Type actions ──────────────────────────────────────────────────────

  const handleAddCostType = async () => {
    const name = addCostTypeInput.trim();
    if (!name) return;
    setSaving(true);
    await dispatch(createCostType({ name }));
    setAddCostTypeInput(""); setAddCostTypeOpen(false); setSaving(false);
  };

  const handleRenameCostType = async () => {
    if (!editingCostType) return;
    const name = editingCostType.name.trim();
    if (!name) return;
    setSaving(true);
    await dispatch(updateCostType({ id: editingCostType.id, data: { name } }));
    setEditingCostType(null); setSaving(false);
  };

  const handleDeleteCostType = (id) => dispatch(deleteCostType(id));

  // ── Sub-category actions ───────────────────────────────────────────────────

  const handleAddSubCat = async () => {
    const name = addSubCatInput.trim();
    if (!name) return;
    setSaving(true);
    await dispatch(createSubCategory({ name }));
    setAddSubCatInput(""); setAddSubCatOpen(false); setSaving(false);
  };

  const handleRenameSubCat = async () => {
    if (!editingSubCat) return;
    const name = editingSubCat.name.trim();
    if (!name) return;
    setSaving(true);
    await dispatch(updateSubCategory({ id: editingSubCat.id, data: { name } }));
    setEditingSubCat(null); setSaving(false);
  };

  const handleDeleteSubCat = (id) => dispatch(deleteSubCategory(id));

  // ── Category actions ───────────────────────────────────────────────────────

  const openAddModal = () => setCategoryModal({ open: true, mode: "add", data: { name: "", cost_type: "" } });
  const openEditModal = (cat) => setCategoryModal({ open: true, mode: "edit", data: { name: cat.name, cost_type: cat.cost_type ? String(cat.cost_type) : "" }, id: cat.id });
  const closeModal = () => setCategoryModal(EMPTY_CATEGORY_MODAL);

  const handleSaveCategory = async () => {
    const { mode, data, id } = categoryModal;
    const name = data.name.trim();
    if (!name) return;
    setSaving(true);
    const payload = { name, cost_type: data.cost_type || null };
    if (mode === "add") await dispatch(createCategory(payload));
    else await dispatch(updateCategory({ id, data: payload }));
    closeModal(); setPage(1); setSaving(false);
  };

  const handleDeleteCategory = (id) => dispatch(deleteCategory(id));

  // ── Budget actions ─────────────────────────────────────────────────────────

  const toggleExpand = (catId) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
    setAddForms((prev) => ({ ...prev, [catId]: prev[catId] || { ...EMPTY_ADD_FORM } }));
  };

  const updateAddForm = (catId, field, value) => {
    setAddForms((prev) => ({ ...prev, [catId]: { ...(prev[catId] || EMPTY_ADD_FORM), [field]: value } }));
  };

  const handleSaveBudgetEntry = async (categoryId) => {
    const form = addForms[categoryId];
    if (!form?.amount || !budgetPhase) return;
    setSaving(true);
    await dispatch(saveBudget({
      id: null,
      data: {
        phase: parseInt(budgetPhase),
        category: categoryId,
        sub_category: form.sub_category ? parseInt(form.sub_category) : null,
        block: form.block ? parseInt(form.block) : null,
        amount: form.amount,
        currency: form.currency || "EUR",
      },
    }));
    setAddForms((prev) => ({ ...prev, [categoryId]: { ...EMPTY_ADD_FORM } }));
    setSaving(false);
  };

  const handleRemoveBudgetEntry = (budgetId) => {
    dispatch(removeBudget({ id: budgetId, phaseId: budgetPhase }));
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const costTypeColorMap = {};
  costTypes.forEach((ct, i) => { costTypeColorMap[ct.id] = getAccent(i); });

  const filteredCategories = selectedCostType === "all"
    ? categories
    : categories.filter((c) => String(c.cost_type) === selectedCostType);

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedCategories = filteredCategories.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Group existing budgets by category
  const budgetsByCategory = budgets.reduce((acc, b) => {
    if (!acc[b.category]) acc[b.category] = [];
    acc[b.category].push(b);
    return acc;
  }, {});

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <AppBreadcrumb />

          {/* Page Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 32 }}>
            <div>
              <h2 style={{ fontSize: "1.75rem", fontWeight: 800, color: "#002045", letterSpacing: "-0.03em", marginBottom: 4 }}>
                Cost Categories
              </h2>
              <p className="text-muted mb-0" style={{ maxWidth: 520, fontSize: "0.9rem" }}>
                Manage cost types, sub-categories, categories and phase budgets.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {activeTab === "categories" && (
                <button
                  className="btn btn-sm fw-bold"
                  style={{ background: "#002045", color: "white", borderRadius: 8, fontSize: "0.82rem", padding: "7px 14px", border: "none" }}
                  onClick={openAddModal}
                >
                  <i className="ri-add-circle-line me-1" />Add Category
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 28, borderBottom: "1px solid #f0f4ff" }}>
            {[
              { key: "categories", label: "Cost Types & Categories", icon: "ri-price-tag-3-line" },
              { key: "budgets", label: "Phase Budgets", icon: "ri-wallet-3-line" },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: "9px 18px", border: "none", background: "transparent", cursor: "pointer", fontSize: "0.82rem", fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? "#002045" : "#64748b", borderBottom: activeTab === tab.key ? "2px solid #002045" : "2px solid transparent", marginBottom: -1, transition: "all 0.15s" }}>
                <i className={`${tab.icon} me-1`} />{tab.label}
              </button>
            ))}
          </div>

          {/* ── Cost Types & Categories Tab ── */}
          {activeTab === "categories" && (
            <>
              {/* Cost Types */}
              <div style={{ marginBottom: 32 }}>
                <SectionLabel>Cost Types</SectionLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                  {loading ? <div className="text-muted small">Loading…</div> : (
                    <>
                      {costTypes.map((ct, i) => {
                        const accent = getAccent(i);
                        const isEditing = editingCostType?.id === ct.id;
                        const catCount = categories.filter((c) => String(c.cost_type) === String(ct.id)).length;
                        return (
                          <div key={ct.id} style={{ background: "white", borderRadius: 14, border: "1px solid #f0f4ff", boxShadow: "0 2px 12px -2px rgba(0,0,0,0.06)", borderLeft: `4px solid ${accent}`, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 34, height: 34, borderRadius: 8, background: getBadgeBg(accent), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <i className="ri-price-tag-3-line" style={{ color: accent, fontSize: 16 }} />
                              </div>
                              {isEditing ? (
                                <input autoFocus type="text" className="form-control form-control-sm" value={editingCostType.name}
                                  onChange={(e) => setEditingCostType((p) => ({ ...p, name: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === "Enter") handleRenameCostType(); if (e.key === "Escape") setEditingCostType(null); }}
                                  style={{ borderRadius: 6, fontSize: "0.82rem", fontWeight: 600 }} />
                              ) : (
                                <span style={{ fontWeight: 700, color: "#002045", fontSize: "0.9rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ct.name}</span>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span style={{ fontSize: "0.72rem", fontWeight: 600, color: accent, background: getBadgeBg(accent), borderRadius: 20, padding: "2px 10px" }}>
                                {catCount} {catCount === 1 ? "Category" : "Categories"}
                              </span>
                              <div style={{ display: "flex", gap: 4 }}>
                                {isEditing ? (
                                  <>
                                    <button className="btn btn-sm p-0" disabled={saving} onClick={handleRenameCostType} style={{ color: "#059669", fontSize: 15 }}><i className="ri-check-line" /></button>
                                    <button className="btn btn-sm p-0" onClick={() => setEditingCostType(null)} style={{ color: "#94a3b8", fontSize: 15 }}><i className="ri-close-line" /></button>
                                  </>
                                ) : (
                                  <>
                                    <button className="btn btn-sm p-0" onClick={() => setEditingCostType({ id: ct.id, name: ct.name })} style={{ color: "#64748b", fontSize: 15 }}><i className="ri-pencil-line" /></button>
                                    <button className="btn btn-sm p-0" onClick={() => handleDeleteCostType(ct.id)} style={{ color: "#ef4444", fontSize: 15, opacity: 0.75 }}><i className="ri-delete-bin-line" /></button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* Ghost Add Cost Type */}
                      <div style={{ background: "white", borderRadius: 14, border: "2px dashed #cbd5e1", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10, cursor: addCostTypeOpen ? "default" : "pointer" }}
                        onClick={() => { if (!addCostTypeOpen) setAddCostTypeOpen(true); }}>
                        {addCostTypeOpen ? (
                          <>
                            <input autoFocus type="text" className="form-control form-control-sm" placeholder="Cost type name…" value={addCostTypeInput}
                              onChange={(e) => setAddCostTypeInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleAddCostType(); if (e.key === "Escape") { setAddCostTypeOpen(false); setAddCostTypeInput(""); } }}
                              style={{ borderRadius: 6, fontSize: "0.82rem" }} />
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="btn btn-sm fw-bold" disabled={saving || !addCostTypeInput.trim()} onClick={(e) => { e.stopPropagation(); handleAddCostType(); }} style={{ background: "#002045", color: "white", borderRadius: 6, fontSize: "0.75rem", padding: "4px 12px", border: "none" }}>Confirm</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); setAddCostTypeOpen(false); setAddCostTypeInput(""); }} style={{ borderRadius: 6, fontSize: "0.75rem", padding: "4px 10px" }}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#94a3b8" }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, border: "2px dashed #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <i className="ri-add-line" style={{ fontSize: 18 }} />
                            </div>
                            <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Add New Cost Type</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Sub-categories */}
              <div style={{ marginBottom: 32 }}>
                <SectionLabel>Sub-categories</SectionLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
                  {loading ? <div className="text-muted small">Loading…</div> : (
                    <>
                      {subCategories.map((sc) => {
                        const isEditing = editingSubCat?.id === sc.id;
                        return (
                          <div key={sc.id} style={{ background: "white", borderRadius: 12, border: "1px solid #f0f4ff", boxShadow: "0 2px 8px -2px rgba(0,0,0,0.05)", padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                            <i className="ri-list-check-3" style={{ color: "#0891b2", fontSize: 16, flexShrink: 0 }} />
                            {isEditing ? (
                              <input autoFocus type="text" className="form-control form-control-sm" value={editingSubCat.name}
                                onChange={(e) => setEditingSubCat((p) => ({ ...p, name: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") handleRenameSubCat(); if (e.key === "Escape") setEditingSubCat(null); }}
                                style={{ borderRadius: 6, fontSize: "0.8rem", fontWeight: 600 }} />
                            ) : (
                              <span style={{ fontWeight: 600, color: "#002045", fontSize: "0.82rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sc.name}</span>
                            )}
                            <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                              {isEditing ? (
                                <>
                                  <button className="btn btn-sm p-0" disabled={saving} onClick={handleRenameSubCat} style={{ color: "#059669", fontSize: 14 }}><i className="ri-check-line" /></button>
                                  <button className="btn btn-sm p-0" onClick={() => setEditingSubCat(null)} style={{ color: "#94a3b8", fontSize: 14 }}><i className="ri-close-line" /></button>
                                </>
                              ) : (
                                <>
                                  <button className="btn btn-sm p-0" onClick={() => setEditingSubCat({ id: sc.id, name: sc.name })} style={{ color: "#64748b", fontSize: 14 }}><i className="ri-pencil-line" /></button>
                                  <button className="btn btn-sm p-0" onClick={() => handleDeleteSubCat(sc.id)} style={{ color: "#ef4444", fontSize: 14, opacity: 0.75 }}><i className="ri-delete-bin-line" /></button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {/* Ghost Add Sub-category */}
                      <div style={{ background: "white", borderRadius: 12, border: "2px dashed #cbd5e1", padding: "12px 14px", display: "flex", alignItems: "center", gap: 8, cursor: addSubCatOpen ? "default" : "pointer" }}
                        onClick={() => { if (!addSubCatOpen) setAddSubCatOpen(true); }}>
                        {addSubCatOpen ? (
                          <>
                            <input autoFocus type="text" className="form-control form-control-sm" placeholder="Sub-category name…" value={addSubCatInput}
                              onChange={(e) => setAddSubCatInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleAddSubCat(); if (e.key === "Escape") { setAddSubCatOpen(false); setAddSubCatInput(""); } }}
                              style={{ borderRadius: 6, fontSize: "0.8rem" }} />
                            <button className="btn btn-sm p-0 ms-1" disabled={saving || !addSubCatInput.trim()} onClick={(e) => { e.stopPropagation(); handleAddSubCat(); }} style={{ color: "#059669", fontSize: 16 }}><i className="ri-check-line" /></button>
                            <button className="btn btn-sm p-0" onClick={(e) => { e.stopPropagation(); setAddSubCatOpen(false); setAddSubCatInput(""); }} style={{ color: "#94a3b8", fontSize: 16 }}><i className="ri-close-line" /></button>
                          </>
                        ) : (
                          <>
                            <i className="ri-add-line" style={{ color: "#94a3b8", fontSize: 16 }} />
                            <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#94a3b8" }}>Add Sub-category</span>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Categories Table */}
              <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f0f4ff", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <h5 className="fw-bold mb-0" style={{ color: "#002045", fontSize: "1rem" }}>All Categories</h5>
                    <p className="text-muted small mb-0 mt-1">{filteredCategories.length} entr{filteredCategories.length === 1 ? "y" : "ies"}</p>
                  </div>
                  <select className="form-select form-select-sm" value={selectedCostType} onChange={(e) => { setSelectedCostType(e.target.value); setPage(1); }}
                    style={{ borderRadius: 8, fontSize: "0.82rem", maxWidth: 200, border: "1px solid #e2e8f0", color: "#002045", fontWeight: 500 }}>
                    <option value="all">All Cost Types</option>
                    {costTypes.map((ct) => <option key={ct.id} value={String(ct.id)}>{ct.name}</option>)}
                  </select>
                </div>
                {loading ? <div className="text-center text-muted py-5 small">Loading…</div>
                  : filteredCategories.length === 0 ? (
                    <div className="text-center text-muted py-5 small"><i className="ri-folder-open-line d-block mb-2" style={{ fontSize: 28 }} />No categories found</div>
                  ) : (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #f0f4ff" }}>
                            {["Category Name", "Cost Type", ""].map((h, i) => (
                              <th key={i} style={{ padding: "10px 24px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#64748b", textAlign: i === 2 ? "right" : "left", width: i === 0 ? "50%" : undefined }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCategories.map((cat) => {
                            const ctIndex = costTypes.findIndex((ct) => String(ct.id) === String(cat.cost_type));
                            const accent = ctIndex >= 0 ? getAccent(ctIndex) : "#94a3b8";
                            const badgeBg = ctIndex >= 0 ? getBadgeBg(accent) : "#f1f5f9";
                            return (
                              <CategoryRow key={cat.id} cat={cat} accent={accent} badgeBg={badgeBg}
                                ctName={cat.cost_type_detail?.name || null}
                                onEdit={() => openEditModal(cat)} onDelete={() => handleDeleteCategory(cat.id)} />
                            );
                          })}
                        </tbody>
                      </table>
                      {totalPages > 1 && (
                        <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f4ff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span className="text-muted" style={{ fontSize: "0.78rem" }}>Page {safePage} of {totalPages}</span>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button className="btn btn-sm btn-outline-secondary" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)} style={{ borderRadius: 6, fontSize: "0.78rem", padding: "4px 10px" }}><i className="ri-arrow-left-s-line" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                              <button key={p} className="btn btn-sm" onClick={() => setPage(p)} style={{ borderRadius: 6, fontSize: "0.78rem", padding: "4px 10px", background: p === safePage ? "#002045" : "white", color: p === safePage ? "white" : "#64748b", border: `1px solid ${p === safePage ? "#002045" : "#dee2e6"}`, fontWeight: p === safePage ? 700 : 400 }}>{p}</button>
                            ))}
                            <button className="btn btn-sm btn-outline-secondary" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)} style={{ borderRadius: 6, fontSize: "0.78rem", padding: "4px 10px" }}><i className="ri-arrow-right-s-line" /></button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
              </div>
            </>
          )}

          {/* ── Phase Budgets Tab ── */}
          {activeTab === "budgets" && (
            <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
              {/* Phase selector */}
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #f0f4ff", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "#002045", fontSize: "1rem" }}>Phase Budgets</h5>
                  <p className="text-muted small mb-0 mt-1">Set budget per category · sub-category · block</p>
                </div>
                <select className="form-select form-select-sm ms-auto" value={budgetPhase} onChange={(e) => setBudgetPhase(e.target.value)}
                  style={{ borderRadius: 8, fontSize: "0.82rem", maxWidth: 240, border: "1px solid #e2e8f0", color: "#002045", fontWeight: 500 }}>
                  <option value="">— Select a phase —</option>
                  {phases.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </div>

              {!budgetPhase ? (
                <div className="text-center text-muted py-5 small"><i className="ri-map-pin-time-line d-block mb-2" style={{ fontSize: 28 }} />Select a phase to manage budgets</div>
              ) : budgetsLoading ? (
                <div className="text-center text-muted py-5 small">Loading…</div>
              ) : categories.length === 0 ? (
                <div className="text-center text-muted py-5 small">No categories found</div>
              ) : (
                <div>
                  {categories.map((cat) => {
                    const ctIndex = costTypes.findIndex((ct) => String(ct.id) === String(cat.cost_type));
                    const accent = ctIndex >= 0 ? getAccent(ctIndex) : "#94a3b8";
                    const isExpanded = expandedCats.has(cat.id);
                    const entries = budgetsByCategory[cat.id] || [];
                    const form = addForms[cat.id] || EMPTY_ADD_FORM;

                    return (
                      <BudgetCategoryAccordion
                        key={cat.id}
                        cat={cat}
                        accent={accent}
                        ctName={cat.cost_type_detail?.name || null}
                        isExpanded={isExpanded}
                        entries={entries}
                        subCategories={subCategories}
                        phaseBlocks={phaseBlocks}
                        form={form}
                        saving={saving}
                        onToggle={() => toggleExpand(cat.id)}
                        onFormChange={(field, value) => updateAddForm(cat.id, field, value)}
                        onSaveEntry={() => handleSaveBudgetEntry(cat.id)}
                        onRemoveEntry={handleRemoveBudgetEntry}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Container>
      </div>

      {/* Category Modal */}
      <Modal isOpen={categoryModal.open} toggle={closeModal} centered size="md">
        <ModalHeader toggle={closeModal} style={{ borderBottom: "1px solid #f0f4ff", paddingBottom: 14 }}>
          <span style={{ fontWeight: 700, color: "#002045", fontSize: "1rem" }}>
            {categoryModal.mode === "add" ? "Add New Category" : "Edit Category"}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: "24px" }}>
          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: "0.82rem", color: "#374151" }}>Category Name</label>
            <input type="text" className="form-control" placeholder="e.g. Foundation Works" value={categoryModal.data.name}
              onChange={(e) => setCategoryModal((prev) => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveCategory(); }}
              style={{ borderRadius: 8, fontSize: "0.875rem" }} />
          </div>
          <div>
            <label className="form-label fw-semibold" style={{ fontSize: "0.82rem", color: "#374151" }}>Cost Type</label>
            <select className="form-select" value={categoryModal.data.cost_type}
              onChange={(e) => setCategoryModal((prev) => ({ ...prev, data: { ...prev.data, cost_type: e.target.value } }))}
              style={{ borderRadius: 8, fontSize: "0.875rem" }}>
              <option value="">— None —</option>
              {costTypes.map((ct) => <option key={ct.id} value={String(ct.id)}>{ct.name}</option>)}
            </select>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #f0f4ff", gap: 8 }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={closeModal} style={{ borderRadius: 8, fontSize: "0.82rem", padding: "7px 16px" }}>Cancel</button>
          <button className="btn btn-sm fw-bold" disabled={saving || !categoryModal.data.name.trim()} onClick={handleSaveCategory}
            style={{ background: "#002045", color: "white", borderRadius: 8, fontSize: "0.82rem", padding: "7px 20px", border: "none" }}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving…</> : "Save"}
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

// ─── CategoryRow ──────────────────────────────────────────────────────────────

const CategoryRow = ({ cat, accent, badgeBg, ctName, onEdit, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <tr onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ borderBottom: "1px solid #f8fafc", background: hovered ? "#f8fafc" : "white", transition: "background 0.12s" }}>
      <td style={{ padding: "12px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 3, height: 28, borderRadius: 3, background: accent, flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: "#002045", fontSize: "0.875rem" }}>{cat.name}</span>
        </div>
      </td>
      <td style={{ padding: "12px 16px" }}>
        {ctName
          ? <span style={{ fontSize: "0.72rem", fontWeight: 600, color: accent, background: badgeBg, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>{ctName}</span>
          : <span className="text-muted" style={{ fontSize: "0.78rem" }}>—</span>}
      </td>
      <td style={{ padding: "12px 24px", textAlign: "right" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", opacity: hovered ? 1 : 0, transition: "opacity 0.12s" }}>
          <button className="btn btn-sm p-0" onClick={onEdit} style={{ color: "#64748b", fontSize: 15 }}><i className="ri-pencil-line" /></button>
          <button className="btn btn-sm p-0" onClick={onDelete} style={{ color: "#ef4444", fontSize: 15, opacity: 0.8 }}><i className="ri-delete-bin-line" /></button>
        </div>
      </td>
    </tr>
  );
};

// ─── BudgetCategoryAccordion ──────────────────────────────────────────────────

const BudgetCategoryAccordion = ({ cat, accent, ctName, isExpanded, entries, subCategories, phaseBlocks, form, saving, onToggle, onFormChange, onSaveEntry, onRemoveEntry }) => {
  const [hovered, setHovered] = useState(false);
  const [displayAmount, setDisplayAmount] = useState(() => formatWithCommas(form.amount));

  useEffect(() => {
    setDisplayAmount(formatWithCommas(form.amount));
  }, [form.amount]);

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
    setDisplayAmount(formatWithCommas(raw));
    onFormChange("amount", raw);
  };

  return (
    <div style={{ borderBottom: "1px solid #f0f4ff" }}>
      {/* Accordion header */}
      <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 24px", cursor: "pointer", background: hovered ? "#f8fafc" : "white", transition: "background 0.12s", userSelect: "none" }}>
        <div style={{ width: 3, height: 28, borderRadius: 3, background: accent, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 600, color: "#002045", fontSize: "0.9rem" }}>{cat.name}</span>
          {ctName && <span style={{ marginLeft: 10, fontSize: "0.7rem", fontWeight: 600, color: accent, background: "#f0f4ff", borderRadius: 20, padding: "2px 8px" }}>{ctName}</span>}
        </div>
        <span style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 500 }}>
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </span>
        <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line`} style={{ color: "#94a3b8", fontSize: 18 }} />
      </div>

      {/* Accordion body */}
      {isExpanded && (
        <div style={{ background: "#fafbfc", borderTop: "1px solid #f0f4ff", padding: "0 24px 16px" }}>

          {/* Existing entries */}
          {entries.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, marginTop: 12 }}>
              <thead>
                <tr>
                  {["Sub-category", "Block", "Amount", "Currency", ""].map((h, i) => (
                    <th key={i} style={{ padding: "6px 8px", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", textAlign: i >= 4 ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid #f0f4ff" }}>
                    <td style={{ padding: "8px 8px", fontSize: "0.82rem", color: "#002045" }}>{entry.sub_category_detail?.name || <span className="text-muted">—</span>}</td>
                    <td style={{ padding: "8px 8px", fontSize: "0.82rem", color: "#002045" }}>{entry.block_detail?.name || <span className="text-muted">—</span>}</td>
                    <td style={{ padding: "8px 8px", fontSize: "0.82rem", fontWeight: 600, color: "#002045" }}>{formatWithCommas(entry.amount)}</td>
                    <td style={{ padding: "8px 8px", fontSize: "0.82rem", color: "#64748b" }}>{entry.currency}</td>
                    <td style={{ padding: "8px 8px", textAlign: "right" }}>
                      <button className="btn btn-sm p-0" onClick={() => onRemoveEntry(entry.id)} style={{ color: "#ef4444", fontSize: 14, opacity: 0.8 }}><i className="ri-delete-bin-line" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add new entry form */}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", paddingTop: entries.length > 0 ? 4 : 12 }}>
            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 4 }}>Sub-category</label>
              <select className="form-select form-select-sm" value={form.sub_category} onChange={(e) => onFormChange("sub_category", e.target.value)}
                style={{ borderRadius: 6, fontSize: "0.8rem" }}>
                <option value="">— None —</option>
                {subCategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 4 }}>Block</label>
              <select className="form-select form-select-sm" value={form.block} onChange={(e) => onFormChange("block", e.target.value)}
                style={{ borderRadius: 6, fontSize: "0.8rem" }} disabled={phaseBlocks.length === 0}>
                <option value="">— None —</option>
                {phaseBlocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div style={{ flex: "1 1 120px" }}>
              <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 4 }}>Amount</label>
              <input type="text" inputMode="decimal" className="form-control form-control-sm" placeholder="0" value={displayAmount} onChange={handleAmountChange}
                style={{ borderRadius: 6, fontSize: "0.8rem" }} />
            </div>
            <div style={{ flex: "0 0 80px" }}>
              <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 4 }}>Currency</label>
              <select className="form-select form-select-sm" value={form.currency} onChange={(e) => onFormChange("currency", e.target.value)}
                style={{ borderRadius: 6, fontSize: "0.8rem" }}>
                <option value="EUR">EUR</option>
                <option value="RON">RON</option>
              </select>
            </div>
            <button className="btn btn-sm fw-bold" disabled={!form.amount || saving} onClick={onSaveEntry}
              style={{ background: "#002045", color: "white", borderRadius: 6, fontSize: "0.75rem", padding: "6px 14px", border: "none", flexShrink: 0, marginBottom: 1 }}>
              <i className="ri-add-line me-1" />Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostCategories;
